import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
    ReassortmentFlow,
    useAbortReassortmentStockTransfersForDateMutation,
    useGetNextMsDeliveryDateQuery,
    useValidateMicrostoresReassortmentMutation,
    TotadminPermission,
} from 'data/__generated__';

import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { Loader, LoaderModeType } from 'components/Loader';
import { TotemDatePicker } from 'components/TotemDatePicker';

import { PAGES } from 'constants/pages';
import { dateFromString, formatDateAsAnniversary, formatDateForDisplay } from 'helpers/dateTimes';
import { confirmPopup } from 'components/Popups/Confirm';

import { ColumnSelectionSection, type SelectableColumn } from './ColumnSelectionSection';

const REASSORTMENT_FLOWS = [ReassortmentFlow.DryAndFruit, ReassortmentFlow.Fresh];

export function StockTransferGenerationPopup({
    isOpen,
    setIsOpen,
    initialColumns = [],
    siteId,
    isLocked = false,
    showOpenSitesShortcut = false,
}: {
    isOpen: boolean;
    setIsOpen: (arg: boolean) => void;
    initialColumns?: SelectableColumn[];
    siteId?: string;
    isLocked?: boolean;
    showOpenSitesShortcut?: boolean;
}) {
    const {
        data: nextMSDeliveryDateData,
        loading: nextMSDeliveryDateLoading,
        error: nextMSDeliveryDateError,
    } = useGetNextMsDeliveryDateQuery();

    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    // Seeded once at mount — every caller mounts this popup conditionally (`{isOpen ? <Popup/> : null}`).
    const [columns, setColumns] = useState<SelectableColumn[]>(initialColumns);

    useEffect(() => {
        if (nextMSDeliveryDateData?.getNextMSDeliveryDate) {
            setSelectedDate(dateFromString(nextMSDeliveryDateData.getNextMSDeliveryDate));
        }
    }, [nextMSDeliveryDateData]);

    const [validateMicrostoresReassortmentMutation, { loading: validateMicrostoreReassortmentLoading }] =
        useValidateMicrostoresReassortmentMutation();

    const [abortReassortmentStockTransfersForDateMutation, { loading: abortReassortmentLoading }] =
        useAbortReassortmentStockTransfersForDateMutation();

    if (nextMSDeliveryDateLoading) {
        return (
            <TotemPopup title="Transfert de stocks" isOpen={isOpen} setIsOpen={setIsOpen} contentOverflow="auto">
                <Loader mode={LoaderModeType.Spin} />
            </TotemPopup>
        );
    }

    if (nextMSDeliveryDateError) {
        throw new Error('Une erreur est survenue lors de la récupération de la prochaine date de livraison');
    }

    const columnIds = columns.map(({ _id }) => _id);
    const hasColumns = columns.length > 0;

    async function validateMicrostoreReassortment(reassortmentFlow: ReassortmentFlow) {
        if (!selectedDate) {
            toast.error('Veuillez sélectionner une date de livraison');
            return;
        }

        const hasConfirmed = await confirmPopup({
            title: 'Confirmer',
            message: 'Vous allez créer ou updater un transfert de stock, êtes vous sûr ?',
        });
        if (!hasConfirmed) {
            return;
        }

        const { data } = await validateMicrostoresReassortmentMutation({
            variables: {
                reassortmentFlow,
                microstoreColumnIds: columnIds,
                siteIds: [],
                reassortmentDate: formatDateAsAnniversary({ dateTime: selectedDate }),
            },
        });

        if (!data) {
            throw new Error('Une erreur est survenue lors de la génération du transfert de stock');
        }

        const {
            validateMicrostoresReassortmentMutation: { errors, microstoreColumns },
        } = data;

        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });

            if (errors.length > 1) {
                toast.info('Cliquez pour fermer toutes les notifications', {
                    autoClose: false,
                    onClick: () => toast.dismiss(),
                });
            }
        }

        if (microstoreColumns) {
            toast.success(
                `Le(s) transfert(s) de stock a/ont bien été créé(s) / modifié(s) ! (Nb : ${microstoreColumns.length})`,
            );
        }
    }

    async function abortReassortmentStockTransfers(reassortmentFlow: ReassortmentFlow) {
        if (!selectedDate) {
            toast.error('Veuillez sélectionner une date de livraison');
            return;
        }

        const flowLabel = `Réassort ${reassortmentFlow}`;
        const reassortmentDate = formatDateAsAnniversary({ dateTime: selectedDate });

        const hasConfirmed = await confirmPopup({
            title: 'Confirmer',
            message: `Vous allez annuler les transferts de stock ${flowLabel} pour le ${formatDateForDisplay(reassortmentDate)} sur la/les colonne(s) sélectionnée(s) (les box préparées seront annulées et les stocks reversés). Continuer ?`,
        });
        if (!hasConfirmed) {
            return;
        }

        const { data } = await abortReassortmentStockTransfersForDateMutation({
            variables: {
                reassortmentDate,
                reassortmentFlow,
                microstoreColumnIds: columnIds,
            },
        });

        if (!data) {
            toast.error("Une erreur est survenue lors de l'annulation des transferts de stock");
            return;
        }

        const { errors, stockTransfers } = data.abortReassortmentStockTransfersForDate;
        if (errors.length) {
            errors.forEach((error, index) => {
                toast.error(<span key={index}>Erreur : {error}</span>, { autoClose: false });
            });
        }
        if (stockTransfers.length) {
            toast.success(`${stockTransfers.length} transfert(s) de stock ${flowLabel} annulé(s)`);
        } else if (!errors.length) {
            toast.info(`Aucun transfert de stock ${flowLabel} à annuler pour cette date`);
        }
    }

    return (
        <TotemPopup title="Transfert de stocks" isOpen={isOpen} setIsOpen={setIsOpen} contentOverflow="auto">
            <Content>
                <Section>
                    <SectionTitle>Date de livraison</SectionTitle>
                    <TotemDatePicker
                        selected={selectedDate}
                        onChange={(date) => setSelectedDate(date)}
                        label="Date de livraison"
                        isDisabled={false}
                    />
                    <StockTransferListLink to={PAGES.stockTransfers.url}>
                        Voir la liste des transferts de stock
                    </StockTransferListLink>
                </Section>
                <ColumnSelectionSection
                    columns={columns}
                    onColumnsChange={setColumns}
                    siteId={siteId}
                    isLocked={isLocked}
                    showOpenSitesShortcut={showOpenSitesShortcut}
                    nextMSDeliveryDate={nextMSDeliveryDateData?.getNextMSDeliveryDate}
                />
                <Section>
                    <SectionTitle>Génération des transferts de stock</SectionTitle>
                    <FlowsContainer>
                        {REASSORTMENT_FLOWS.map((reassortmentFlow) => {
                            const flowLabel = `Réassort ${reassortmentFlow}`;
                            return (
                                <FlowContainer key={reassortmentFlow}>
                                    <span>Transfert de stock pour : {flowLabel}</span>
                                    <TotemPrimaryLoadingButton
                                        type="button"
                                        title="Créer ou modifier les transferts de stock des colonnes sélectionnées"
                                        loading={validateMicrostoreReassortmentLoading}
                                        disabled={!hasColumns}
                                        onClick={() => validateMicrostoreReassortment(reassortmentFlow)}
                                        useIcon={false}
                                        label={`Créer ou modifier pour ${flowLabel}`}
                                        requiredPermissions={[TotadminPermission.MicrostoreColumnsManage]}
                                    />
                                    <TotemPrimaryLoadingButton
                                        type="button"
                                        title={`Annuler les S.T. ${flowLabel} des colonnes sélectionnées`}
                                        loading={abortReassortmentLoading}
                                        disabled={!hasColumns}
                                        onClick={() => abortReassortmentStockTransfers(reassortmentFlow)}
                                        useIcon={false}
                                        $isDangerStyle
                                        label={`Annuler les S.T. ${flowLabel}`}
                                        requiredPermissions={[TotadminPermission.StockTransfersManage]}
                                    />
                                </FlowContainer>
                            );
                        })}
                    </FlowsContainer>
                </Section>
            </Content>
        </TotemPopup>
    );
}

const Content = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;

    & > :not(:first-child) {
        margin-top: 10px;
        border-top: 1px solid ${({ theme }) => theme.darkBorderColor};
    }
`;

const Section = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 5px;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;

const SectionTitle = styled.div`
    text-align: center;
    font-size: 20px;
    font-weight: 800;
`;

const StockTransferListLink = styled(Link)`
    text-align: center;
    font-weight: 600;
    color: ${({ theme }) => theme.ctaPrimaryColor};
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }
`;

const FlowsContainer = styled.div`
    display: flex;
    flex-direction: row;

    & > :not(:first-child) {
        margin-left: 20px;
    }
`;

const FlowContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
